import { useMemo } from "react";
import { Vector2 } from "three";
import { positionCode } from "../domain/layout";
import {
  ALLOY,
  BRAKE_DISC,
  BRAKE_DRUM,
  CALIPER,
  CAVITY,
  HIGHLIGHT_COLOR,
  HUB,
  RIM_BARREL,
  RUBBER,
  STEEL_WHEEL,
  TREAD_GROOVE
} from "./materials";
const RIM = {
  barrel: 0.624,
  lip: 0.576,
  lipTube: 0.05,
  spokeRing: 0.347,
  spoke: [0.397, 0.082, 0.147],
  hub: 0.162,
  hubLength: 0.294,
  cap: 0.106,
  capLength: 0.338,
  plate: 0.62,
  plateThickness: 0.05,
  vent: 0.085,
  ventRing: 0.4,
  hubCap: 0.21,
  hubCapLength: 0.16
};
const BRAKE = {
  disc: 0.494,
  discThickness: 0.047,
  caliperRing: 0.426,
  caliper: [0.162, 0.265, 0.324],
  drum: 0.56
};
const BARE = {
  flange: 0.229,
  flangeThickness: 0.088,
  studRing: 0.153,
  stud: 0.0265,
  studLength: 0.147
};
const TREAD = { radius: 0.975, tube: 0.038 };
const HOVER_RING = { radius: 0.78, tube: 0.035 };
const SELECTED_RING = { radius: 0.8, tube: 0.075 };
const OVERLAY = { overlay: true };
const RING_MATERIAL = {
  color: HIGHLIGHT_COLOR,
  toneMapped: false,
  /*
   * Drawn through the bodywork on purpose. At most one wheel is hovered and one selected,
   * and a ring that hides behind a tipper body — or behind its own outer twin — cannot
   * answer the only question it is there to answer: which one.
   */
  depthTest: false,
  depthWrite: false
};
const SPOKE_COUNT = 5;
const VENT_COUNT = 8;
const STUD_COUNT = { alloy: 5, steel: 10 };
const ringPositions = (count) => Array.from({ length: count }, (_, i) => i);
const tyreCrossSection = (radius, halfWidth) => [
  [0.632, -1],
  [0.735, -1],
  [0.841, -0.933],
  [0.929, -0.8],
  [0.982, -0.622],
  [1, -0.356],
  [1, 0.356],
  [0.982, 0.622],
  [0.929, 0.8],
  [0.841, 0.933],
  [0.735, 1],
  [0.632, 1]
].map(([r, w]) => new Vector2(radius * r, halfWidth * w));
function Wheel({
  placement,
  fitted,
  statusColor,
  hovered,
  selected,
  onHoverStart,
  onHoverEnd,
  onSelect
}) {
  const { ref, position, radius, width, style = "alloy" } = placement;
  const halfWidth = width / 2;
  const profile = useMemo(() => tyreCrossSection(radius, halfWidth), [radius, halfWidth]);
  const code = positionCode(ref);
  const rotationX = ref.side === "right" ? Math.PI / 2 : -Math.PI / 2;
  const showsBrake = ref.slot !== "outer";
  const isFrontmost = (event) => {
    var _a;
    return ((_a = event.intersections[0]) == null ? void 0 : _a.eventObject) === event.eventObject;
  };
  const handleOver = (event) => {
    if (!isFrontmost(event)) return;
    event.stopPropagation();
    onHoverStart(ref);
  };
  const handleOut = () => onHoverEnd(ref);
  const handleDown = (event) => {
    if (!isFrontmost(event)) return;
    event.stopPropagation();
    onSelect(ref);
  };
  return <group
    name={code}
    position={position}
    rotation={[rotationX, 0, 0]}
    onPointerOver={handleOver}
    onPointerOut={handleOut}
    onPointerDown={handleDown}
  >
      {fitted && <>
          <mesh>
            <latheGeometry args={[profile, 64]} />
            <meshPhysicalMaterial {...RUBBER} color={statusColor || RUBBER.color} />
          </mesh>

          {
    /* circumferential grooves read as tread from any orbit angle */
  }
          {[-0.49, 0, 0.49].map((offset) => <mesh
    key={`groove-${offset}`}
    position={[0, halfWidth * offset, 0]}
    rotation={[Math.PI / 2, 0, 0]}
  >
              <torusGeometry args={[radius * TREAD.radius, radius * TREAD.tube, 8, 64]} />
              <meshPhysicalMaterial {...TREAD_GROOVE} />
            </mesh>)}

          <mesh>
            <cylinderGeometry
    args={[radius * RIM.barrel, radius * RIM.barrel, halfWidth * 1.7, 48]}
  />
            <meshPhysicalMaterial {...RIM_BARREL} />
          </mesh>

          {style === "alloy" ? <>
              <mesh position={[0, halfWidth * 0.82, 0]} rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[radius * RIM.lip, radius * RIM.lipTube, 10, 64]} />
                <meshPhysicalMaterial {...ALLOY} />
              </mesh>

              {ringPositions(SPOKE_COUNT).map((i) => {
    const angle = i * Math.PI * 2 / SPOKE_COUNT;
    const r = radius * RIM.spokeRing;
    return <mesh
      key={`spoke-${i}`}
      position={[Math.cos(angle) * r, halfWidth * 0.82, Math.sin(angle) * r]}
      rotation={[0, -angle, 0]}
    >
                    <boxGeometry
      args={[radius * RIM.spoke[0], radius * RIM.spoke[1], radius * RIM.spoke[2]]}
    />
                    <meshPhysicalMaterial {...ALLOY} />
                  </mesh>;
  })}

              <mesh position={[0, halfWidth * 0.6, 0]}>
                <cylinderGeometry
    args={[radius * RIM.hub, radius * RIM.hub, radius * RIM.hubLength, 24]}
  />
                <meshPhysicalMaterial {...ALLOY} />
              </mesh>
              <mesh position={[0, halfWidth * 0.72, 0]}>
                <cylinderGeometry
    args={[radius * RIM.cap, radius * RIM.cap, radius * RIM.capLength, 20]}
  />
                <meshPhysicalMaterial {...HUB} />
              </mesh>
            </> : <>
              {
    /* flat pressed disc, with hand holes rather than spokes */
  }
              <mesh position={[0, halfWidth * 0.7, 0]}>
                <cylinderGeometry
    args={[radius * RIM.plate, radius * RIM.plate, radius * RIM.plateThickness, 48]}
  />
                <meshPhysicalMaterial {...STEEL_WHEEL} />
              </mesh>

              {ringPositions(VENT_COUNT).map((i) => {
    const angle = i * Math.PI * 2 / VENT_COUNT;
    const r = radius * RIM.ventRing;
    return <mesh
      key={`vent-${i}`}
      position={[Math.cos(angle) * r, halfWidth * 0.7, Math.sin(angle) * r]}
    >
                    <cylinderGeometry
      args={[radius * RIM.vent, radius * RIM.vent, radius * 0.12, 20]}
    />
                    <meshPhysicalMaterial {...CAVITY} />
                  </mesh>;
  })}

              <mesh position={[0, halfWidth * 0.8, 0]}>
                <cylinderGeometry
    args={[
      radius * RIM.hubCap,
      radius * RIM.hubCap,
      radius * RIM.hubCapLength,
      24
    ]}
  />
                <meshPhysicalMaterial {...HUB} />
              </mesh>
            </>}
        </>}

      {!fitted && <>
          <mesh position={[0, halfWidth * 0.2, 0]}>
            <cylinderGeometry
    args={[
      radius * BARE.flange,
      radius * BARE.flange,
      radius * BARE.flangeThickness,
      28
    ]}
  />
            <meshPhysicalMaterial {...HUB} />
          </mesh>
          {ringPositions(STUD_COUNT[style]).map((i) => {
    const angle = i * Math.PI * 2 / STUD_COUNT[style];
    const r = radius * BARE.studRing;
    return <mesh
      key={`stud-${i}`}
      position={[Math.cos(angle) * r, halfWidth * 0.42, Math.sin(angle) * r]}
    >
                <cylinderGeometry
      args={[radius * BARE.stud, radius * BARE.stud, radius * BARE.studLength, 10]}
    />
                <meshPhysicalMaterial {...HUB} />
              </mesh>;
  })}
        </>}

      {showsBrake && (style === "alloy" ? <>
            <mesh position={[0, halfWidth * 0.05, 0]}>
              <cylinderGeometry
    args={[radius * BRAKE.disc, radius * BRAKE.disc, radius * BRAKE.discThickness, 32]}
  />
              <meshPhysicalMaterial {...BRAKE_DISC} />
            </mesh>
            <mesh
    position={[
      Math.cos(3.58) * radius * BRAKE.caliperRing,
      0,
      Math.sin(3.58) * radius * BRAKE.caliperRing
    ]}
    rotation={[0, -3.58, 0]}
  >
              <boxGeometry
    args={[
      radius * BRAKE.caliper[0],
      radius * BRAKE.caliper[1],
      radius * BRAKE.caliper[2]
    ]}
  />
              <meshPhysicalMaterial {...CALIPER} />
            </mesh>
          </> : <mesh>
            <cylinderGeometry
    args={[radius * BRAKE.drum, radius * BRAKE.drum, halfWidth * 0.55, 32]}
  />
            <meshPhysicalMaterial {...BRAKE_DRUM} />
          </mesh>)}

      {
    /*
      Both rings sit on the wheel face, not around the tyre: a ring outside the tyre is
      swallowed by the wheel arch from almost every angle. They are mounted rather than
      swapped so that neither torus is rebuilt on every hover.
    */
  }
      <mesh
    visible={hovered && !selected}
    position={[0, halfWidth + radius * 0.035, 0]}
    rotation={[Math.PI / 2, 0, 0]}
    renderOrder={2}
    userData={OVERLAY}
  >
        <torusGeometry args={[radius * HOVER_RING.radius, radius * HOVER_RING.tube, 10, 72]} />
        <meshBasicMaterial {...RING_MATERIAL} />
      </mesh>
      <mesh
    visible={selected}
    position={[0, halfWidth + radius * 0.045, 0]}
    rotation={[Math.PI / 2, 0, 0]}
    renderOrder={2}
    userData={OVERLAY}
  >
        <torusGeometry args={[radius * SELECTED_RING.radius, radius * SELECTED_RING.tube, 12, 72]} />
        <meshBasicMaterial {...RING_MATERIAL} />
      </mesh>
    </group>;
}
export {
  Wheel
};
